import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { getFullName } from '@core/util/string';
import { ErrorCodes } from '@fnModels/error-codes';
import { UserTaken } from '@fnModels/llc';
import { LLCFunctions } from '@functions/llc.service';
import { checkErrorCode } from '@globalUtils/utilities';
import { BusinessUser, BusinessUserRole } from '@models/businesses';
import { select, Select, Store } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { ConnectedUser } from '@state/llc/llc.model';
import { LlcState } from '@state/llc/llc.state';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { UserProfileState } from 'src/app/shared/state/user-profile';

enum FORM_VALUES {
	USER_ID = 'userId',
	USERNAME = 'username',
	PASSWORD = 'password',
}

interface LoginFormGroup {
	[FORM_VALUES.USER_ID]: FormControl<string>;
	[FORM_VALUES.USERNAME]: FormControl<string>;
	[FORM_VALUES.PASSWORD]: FormControl<string>;
}

export interface LLCLoginComponentData {
	type?: 'FCT';
}

@Component({
	selector: 'app-llc-login',
	templateUrl: './login.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LLCLoginComponent implements OnInit {
	public ICON = ICON;
	public error: string;
	public userTaken: UserTaken;
	public signInForm: FormGroup<LoginFormGroup>;
	public businessUsersUrl: string;
	public loading$ = new BehaviorSubject(false);
	public BusinessUserRole = BusinessUserRole;
	public lawyerBusinessRole = BusinessUserRole.LAWYER;
	public FORM_VALUES = FORM_VALUES;
	public logoUrl = '/assets/images/integrations/llc-logo.png';
	public name = 'LLC';
	public lawyerOptions$ = new Observable<DropdownOption<BusinessUser>[]>();
	public selectedLawyerName = '';
	public currentBusinessId = select(UserProfileState.getCurrentBusinessId);
	@Select(BusinessUsersState.getLawyers) public allLawyers$: Observable<
		BusinessUser[]
	>;
	@Select(LlcState.areAllLawyersConnected)
	areAllLawyersConnected$!: Observable<boolean>;
	@Select(BusinessUsersState.getLoggedInUserRole)
	public userRole$: BusinessUserRole;
	@Select(LlcState.canLogin) public canLogin$!: Observable<boolean>;
	@Select(LlcState.connectedUsers) public connectedUsers$!: Observable<
		ConnectedUser[]
	>;
	@Select(LlcState.connectedUserIds) public connectedUserIds$!: Observable<
		string[]
	>;
	@Select(LlcState.showInviteOnLoginPage)
	public showInviteOnLoginPage$!: Observable<boolean>;

	constructor(
		public dialogRef: MatDialogRef<LLCLoginComponent>,
		private llcFunctions: LLCFunctions,
		private navigationService: NavigationService,
		private store: Store,
		@Inject(MAT_DIALOG_DATA) private data: LLCLoginComponentData,
	) {}

	ngOnInit(): void {
		this.signInForm = this.createNewSignInForm();
		this.businessUsersUrl = UrlService.Manage_Users();
		if (this.data?.type) {
			if (this.data?.type === 'FCT') {
				this.logoUrl = '/assets/images/integrations/fct-logo.svg';
				this.name = 'FCT';
			}
		}

		this.lawyerOptions$ = combineLatest([
			this.allLawyers$,
			this.connectedUserIds$,
		]).pipe(
			map(([lawyers, connectedIds]) => {
				return lawyers.filter(
					(lawyer) => !connectedIds.some((id) => lawyer.id === id),
				);
			}),
			map((x) => {
				const loggedInUserId = this.store.selectSnapshot(AuthState.getUserId);
				const loggedInUserRole = this.store.selectSnapshot(
					BusinessUsersState.getLoggedInUserRole,
				);

				return x.map((lawyer) => {
					const lawyerName = getFullName([lawyer.firstName, lawyer.lastName]);

					if (
						loggedInUserRole === BusinessUserRole.LAWYER &&
						lawyer.id === loggedInUserId
					) {
						this.selectedLawyerName = lawyerName;
						this.signInForm.patchValue({ userId: lawyer.id });
					}
					const dropDownOption: DropdownOption<BusinessUser> = {
						value: lawyer,
						name:
							lawyer.id === loggedInUserId ? `Me (${lawyerName})` : lawyerName,
					};
					return dropDownOption;
				});
			}),
		);
	}

	private createNewSignInForm(): FormGroup<LoginFormGroup> {
		return new FormGroup({
			[FORM_VALUES.USER_ID]: new FormControl<string>('', [Validators.required]),
			[FORM_VALUES.USERNAME]: new FormControl<string>('', [
				Validators.required,
			]),
			[FORM_VALUES.PASSWORD]: new FormControl<string>('', [
				Validators.required,
			]),
		});
	}

	goToInvite() {
		this.navigationService.goToManage_Invites();
		this.dialogRef.close();
	}

	onLawyerSelect(selectedLawyer: DropdownResult<DropdownOption<BusinessUser>>) {
		this.selectedLawyerName = selectedLawyer.result.name;
		this.signInForm.patchValue({ userId: selectedLawyer.result.value.id });
	}

	cancel(): void {
		this.dialogRef.close();
	}

	async login(): Promise<void> {
		const { signInForm, name } = this;
		const { valid } = signInForm;
		const { username, password, userId } = signInForm.value;

		this.userTaken = null;
		this.error = null;

		if (valid) {
			try {
				this.loading$.next(true);
				await this.llcFunctions
					.login(username, password, userId, this.currentBusinessId())
					.pipe(take(1))
					.toPromise();

				this.dialogRef.close(true);
			} catch (err) {
				if (checkErrorCode(err?.code, ErrorCodes.PERMISSION_DENIED)) {
					this.error = 'Invalid credentials';
				} else if (checkErrorCode(err?.code, ErrorCodes.ALREADY_EXISTS)) {
					this.userTaken = err?.details as UserTaken;
				} else {
					this.error = `Unable to login to ${name} due to unknown issue`;
					LogRocket.log(`Login attempt to ${name} failed`, {
						error: err,
						username,
						userId,
						providedPassword: !!password?.trim()?.length,
					});
				}
			} finally {
				this.loading$.next(false);
			}
		}
	}
}
